// src/components/JobView/Tabs/Modals/ReportFailureModal.js
// Modal único para reportar una falla / devolver a Diseño.
// - Exige asignar la alerta a un USUARIO REAL del sistema (obligatorio).
// - Ofrece una sugerencia de IA opcional (limpia el texto y propone una acción).
// Se usa tanto en la tabla de Prepress como en el modal de detalle de plancha.
import React, { useState, useEffect } from 'react';
import { User, Sparkles, Mail, Loader2 } from 'lucide-react';
import { requestAiSuggestion, userLabel } from '../../../../utils/taskAssignment';

const ReportFailureModal = ({ isOpen, onClose, onSubmit, sheetName, users = [], initialFailureType = 'production', sheetContext = '', initialReason = '' }) => {
    const [failureType, setFailureType] = useState(initialFailureType);
    const [failedCount, setFailedCount] = useState(1);
    const [reason, setReason] = useState(initialReason);
    const [assignedUserId, setAssignedUserId] = useState('');
    // Campos de la sugerencia de IA (editables por el operador antes de confirmar).
    const [aiSuggestion, setAiSuggestion] = useState('');
    const [aiCleaned, setAiCleaned] = useState('');
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFailureType(initialFailureType);
            setFailedCount(1);
            setReason(initialReason);
            setAssignedUserId('');
            setAiSuggestion('');
            setAiCleaned('');
            setAiLoading(false);
        }
    }, [isOpen, initialFailureType, initialReason]);

    if (!isOpen) return null;

    const isDesignError = failureType === 'design';
    const canSubmit = reason.trim() && assignedUserId && (isDesignError || failedCount > 0);

    const handleGenerateAi = async () => {
        if (!reason.trim()) return;
        setAiLoading(true);
        const data = await requestAiSuggestion({
            problem: reason.trim(),
            sheetName,
            sheetContext: `${sheetContext} | Tipo de falla: ${isDesignError ? 'Error de Diseño' : 'Falla de Producción'}`,
        });
        if (data) {
            setAiCleaned(data.cleanedDescription || '');
            setAiSuggestion(data.suggestedAction || '');
        } else {
            setAiSuggestion('No se pudo obtener una sugerencia de IA en este momento.');
        }
        setAiLoading(false);
    };

    const handleConfirm = () => {
        const assignedUser = users.find(u => u.id === assignedUserId) || null;
        onSubmit({
            failureType,
            failedCount,
            reason: reason.trim(),
            assignedUser,
            aiCleanedDescription: aiCleaned.trim(),
            aiSuggestedAction: aiSuggestion.trim(),
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[61] flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b"><h2 className="text-lg font-bold">Reportar Falla: {sheetName}</h2></div>
                <div className="p-6 space-y-4">
                    {/* Selector de tipo de falla */}
                    <div>
                        <label className="font-semibold text-sm block mb-2">Tipo de Falla</label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setFailureType('production')}
                                className={`flex-1 py-2 px-3 rounded-md border text-sm font-semibold transition-colors ${!isDesignError ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            >
                                🖨️ Falla de Producción
                            </button>
                            <button
                                onClick={() => setFailureType('design')}
                                className={`flex-1 py-2 px-3 rounded-md border text-sm font-semibold transition-colors ${isDesignError ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            >
                                ✏️ Error de Diseño
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            {isDesignError
                                ? 'Devuelve la plancha a Diseño (estado "Devuelto a Diseño") y crea una tarea de corrección.'
                                : 'Pone la plancha en estado "Reproducir" y crea una tarea de reproducción.'}
                        </p>
                    </div>

                    {!isDesignError && (
                        <div>
                            <label className="font-semibold text-sm">Cantidad de Planchas Dañadas</label>
                            <input type="number" min="1" value={failedCount} onChange={(e) => setFailedCount(parseInt(e.target.value, 10))} className="w-full p-2 mt-1 border rounded-md"/>
                        </div>
                    )}

                    <div>
                        <label className="font-semibold text-sm">
                            {isDesignError ? 'Motivo de la devolución' : 'Razón de la Falla'}
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows="3"
                            placeholder={isDesignError ? 'Ej: Color incorrecto, dimensiones erróneas...' : 'Ej: Atasco en la impresora, material defectuoso...'}
                            className="w-full p-2 mt-1 border rounded-md"
                        />
                        <button
                            onClick={handleGenerateAi}
                            disabled={!reason.trim() || aiLoading}
                            type="button"
                            className="mt-2 flex items-center gap-2 text-sm px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 disabled:opacity-50"
                        >
                            {aiLoading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>}
                            {aiLoading ? 'Generando...' : 'Mejorar y sugerir con IA'}
                        </button>
                    </div>

                    {(aiCleaned || aiSuggestion) && (
                        <div className="space-y-3 p-3 bg-purple-50 border border-purple-200 rounded-md">
                            <div>
                                <label className="font-semibold text-xs text-purple-800 block mb-1">Descripción mejorada (IA)</label>
                                <textarea value={aiCleaned} onChange={(e) => setAiCleaned(e.target.value)} rows="2" className="w-full p-2 border rounded-md text-sm"/>
                            </div>
                            <div>
                                <label className="font-semibold text-xs text-purple-800 block mb-1">Acción sugerida (IA)</label>
                                <textarea value={aiSuggestion} onChange={(e) => setAiSuggestion(e.target.value)} rows="2" className="w-full p-2 border rounded-md text-sm"/>
                            </div>
                        </div>
                    )}

                    {/* Asignación OBLIGATORIA a un usuario real */}
                    <div>
                        <label className="font-semibold text-sm flex items-center gap-1"><User size={14}/> Asignar a <span className="text-red-600">*</span></label>
                        <select
                            value={assignedUserId}
                            onChange={(e) => setAssignedUserId(e.target.value)}
                            className="w-full p-2 mt-1 border rounded-md bg-white"
                        >
                            <option value="">— Selecciona un responsable —</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{userLabel(u)}</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <Mail size={12}/> Se enviará un correo al usuario asignado.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end p-4 bg-gray-50 space-x-3 sticky bottom-0">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">Cancelar</button>
                    <button
                        onClick={handleConfirm}
                        disabled={!canSubmit}
                        className={`px-4 py-2 text-white font-bold rounded-md disabled:bg-gray-400 ${isDesignError ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-600 hover:bg-red-700'}`}
                    >
                        {isDesignError ? 'Devolver a Diseño' : 'Confirmar y Crear Tarea'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportFailureModal;
